import { BaseService } from '@/modules/core/app/services/abstracts/BaseService';
import BroadcastService from '@/integrations/pusher/BroadcastService';
import { AsyncRequestJobCompleteSocketEvent } from '@/modules/core/api/events/AsyncRequestJobCompleteSocketEvent';
import Lang from '@/modules/core/app/services/Lang';
import { AsyncRequestResponse } from '@/modules/core/api/models/AsyncRequestResponse';
import AsyncRequestResponseService from '@/modules/core/app/services/AsyncRequestResponseService';
import { getters } from '@/modules/core/app/helpers/store';
import { AxiosResponse } from '@/modules/core/app/models/AxiosResponse';
import { AxiosResponseType } from '@/modules/core/app/constants/AxiosResponseType';

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  timeoutId: number;
  responseType: AxiosResponseType;
}

export class AsyncRequestService extends BaseService {
  private pendingSocketRequests: Record<string, PendingRequest> = {};

  init(): void {
    if (!getters.auth.getUser()) {
      return;
    }

    BroadcastService.stopListening(AsyncRequestJobCompleteSocketEvent);

    BroadcastService.listen(
      AsyncRequestJobCompleteSocketEvent,
      this.handleJobCompleteSocketEvent.bind(this) as () => Promise<void>
    );
  }

  addPollingAsyncRequest(
    asyncRequestId: string,
    responseType: AxiosResponseType = AxiosResponseType.JSON,
    pollingFrequencyMs: number = 3000,
    maxPollAttempts: number = 30
  ): Promise<unknown> {
    return new Promise((resolve: (value: unknown) => void, reject: (reason?: unknown) => void) => {
      const attemptPolling: (pollAttempts?: number) => Promise<void> = async (
        pollAttempts: number = 0
      ) => {
        if (pollAttempts >= maxPollAttempts) {
          reject(
            new Error(
              Lang.t('api.request-time-out', {
                timeoutMs: pollingFrequencyMs * maxPollAttempts,
                asyncRequestId
              })
            )
          );
          return;
        }

        let response: AsyncRequestResponse | AxiosResponse | undefined;
        try {
          response = await AsyncRequestResponseService.getRequestResponse(
            asyncRequestId,
            responseType
          );
        } catch (error) {
          reject(error);
        }

        if (!this.isEmptyResponse(response)) {
          this.resolveRequest(resolve, response!);
        } else {
          setTimeout(() => attemptPolling(pollAttempts + 1), pollingFrequencyMs);
        }
      };

      attemptPolling();
    });
  }

  addPendingSocketRequest(
    asyncRequestId: string,
    responseType: AxiosResponseType = AxiosResponseType.JSON,
    timeoutMs: number = 90000
  ): Promise<unknown> {
    return new Promise((resolve: (value: unknown) => void, reject: (reason?: unknown) => void) => {
      const timeoutId: number = window.setTimeout(() => {
        delete this.pendingSocketRequests[asyncRequestId];

        reject(new Error(Lang.t('api.request-time-out', { timeoutMs, asyncRequestId })));
      }, timeoutMs);

      this.pendingSocketRequests[asyncRequestId] = { resolve, reject, timeoutId, responseType };
    });
  }

  async handleJobCompleteSocketEvent(event: AsyncRequestJobCompleteSocketEvent): Promise<void> {
    const pendingRequest: PendingRequest | undefined = this.pendingSocketRequests[event.requestId];

    if (!pendingRequest) {
      return;
    }

    window.clearTimeout(pendingRequest.timeoutId);

    delete this.pendingSocketRequests[event.requestId];

    let response: AsyncRequestResponse | AxiosResponse | undefined;
    try {
      response = await AsyncRequestResponseService.getRequestResponse(
        event.requestId,
        pendingRequest.responseType
      );
    } catch (error) {
      pendingRequest.reject(error);
      return;
    }

    this.resolveRequest(pendingRequest.resolve, response);
  }

  private resolveRequest(
    resolve: (value: unknown) => void,
    response: AsyncRequestResponse | AxiosResponse
  ): void {
    if (response?.data instanceof Blob) {
      resolve(response);
      return;
    }

    resolve(response?.requestData);
  }

  private isEmptyResponse(response: AsyncRequestResponse | AxiosResponse | undefined): boolean {
    if (response?.data instanceof Blob) {
      return response?.data?.size === 0;
    }

    return !response?.requestData;
  }
}

export default new AsyncRequestService();
